import {useCallback, useEffect, useState} from 'react'
import {useForm, FormProvider, Controller} from 'react-hook-form'
import {InputAdornment, Stack, Typography} from '@mui/material'
import PropTypes from 'prop-types'

import Button from '../../../../shared_components/mui_base_components/button/Button'
import MaskTextButton from '../../../../shared_components/forms/MaskTextButton'
import PasswordWithConfirmation from '../../../../shared_components/forms/v3/PasswordWithConfirmation'
import TextInput from '../../../../shared_components/forms/v3/TextInput'
import {passwordUpdateSecurity} from '../../../../shared_assets/100px_icons'
import usePasswordValidation from '../../../../shared_components/hooks/usePasswordValidation'

import jsonStatham from '../../../../lib/jsonStatham'
import MixpanelLogger from '../../../../lib/MixpanelLogger'
import SessionStore from '../../../../auth/src/stores/SessionStore'

import './interstitialUpdatePassword.scss'

const PasswordIntro = ({onContinue}) => (
  <div className='password-intro'>
    <div className='intro-content'>
      <h1>Update password</h1>
      <img alt='Security' className='illustration-mobile' src={passwordUpdateSecurity} />
      <p>
        We've strengthened our password requirements to better protect your account.
        Please create a stronger password now.
      </p>
      <Button onClick={onContinue}>Update password</Button>
    </div>
    <div className='illustration-desktop'>
      <img alt='Security' src={passwordUpdateSecurity} />
    </div>
  </div>
)

PasswordIntro.propTypes = {
  onContinue: PropTypes.func.isRequired
}

const PasswordForm = ({returnToController}) => {
  const [processing, setProcessing] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [apiErrors, setApiErrors] = useState(null)
  const {validatePassword, validating} = usePasswordValidation()

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      current_password: '',
      password: '',
      password_confirmation: ''
    }
  })

  const submit = useCallback(async formData => {
    setApiErrors(null)

    const serverErrors = await validatePassword(formData.password)
    if (serverErrors.length > 0) {
      methods.setError('password', {type: 'server', message: serverErrors[0]})
      return
    }

    setProcessing(true)

    const currentUser = SessionStore.getState().currentUser

    try {
      await jsonStatham.put(`account_settings/${currentUser.id}/update_password`, formData)
      MixpanelLogger.track('completed_mandatory_password_update')
      returnToController()
    } catch (response) {
      const rawErrors = response?.errors
      let errors = []

      if (Array.isArray(rawErrors))
        errors = rawErrors
      else if (rawErrors && typeof rawErrors === 'object')
        errors = Object.values(rawErrors).flat().filter(Boolean)
      else if (typeof rawErrors === 'string')
        errors = [rawErrors]

      if (errors.length === 0)
        errors = ['An error occurred. Please try again.']

      const unmapped = []

      errors.forEach(error => {
        const message = String(error)
        const lower = message.toLowerCase()
        if (lower.includes('current password'))
          methods.setError('current_password', {type: 'server', message})
        else if (lower.includes('confirmation'))
          methods.setError('password_confirmation', {type: 'server', message})
        else if (lower.includes('password'))
          methods.setError('password', {type: 'server', message})
        else
          unmapped.push(message)
      })

      if (unmapped.length > 0)
        setApiErrors(unmapped)
    } finally {
      setProcessing(false)
    }
  }, [validatePassword, methods, returnToController])

  return (
    <div className='password-form-container'>
      <div className='password-form-content'>
        <h1>Update password</h1>
        <img alt='Security' className='illustration-mobile' src={passwordUpdateSecurity} />
        <p>
          Password must be a minimum of 12 characters.
          Consider using a memorable phrase or use a password manager to generate one.
        </p>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(submit)} noValidate>
            <Stack spacing={3}>
              <Controller
                name='current_password'
                control={methods.control}
                rules={{required: 'Please enter your current password.'}}
                render={({field, fieldState: {error}}) => (
                  <TextInput
                    {...field}
                    ariaLabel='Current password'
                    autoComplete='current-password'
                    id='current_password'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <MaskTextButton
                            showText={showCurrentPassword}
                            toggleButton={() => setShowCurrentPassword(prev => !prev)}
                          />
                        </InputAdornment>
                      )
                    }}
                    placeholder='Current password'
                    type={showCurrentPassword ? 'text' : 'password'}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
              <PasswordWithConfirmation ariaLabel='New password' confirmAriaLabel='Confirm password' label='' confirmLabel='' placeholder='New password' confirmPlaceholder='Confirm password' />
              {apiErrors && (
                <Typography variant='body2' color='error'>
                  {Array.isArray(apiErrors) ? apiErrors.join(' ') : apiErrors}
                </Typography>
              )}
              <Button
                type='submit'
                loading={processing || validating}>
                Update password
              </Button>
            </Stack>
          </form>
        </FormProvider>
      </div>
      <div className='illustration-desktop'>
        <img alt='Security' src={passwordUpdateSecurity} />
      </div>
    </div>
  )
}

PasswordForm.propTypes = {
  returnToController: PropTypes.func.isRequired
}

const InterstitialUpdatePassword = ({returnToController}) => {
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    MixpanelLogger.track('view_mandatory_password_update_interstitial')
  }, [])

  return (
    <div className='interstitial-update-password'>
      <div className='central-content-box'>
        {showForm ?
          <PasswordForm returnToController={returnToController} /> :
          <PasswordIntro onContinue={() => setShowForm(true)} />
        }
      </div>
    </div>
  )
}

InterstitialUpdatePassword.propTypes = {
  returnToController: PropTypes.func.isRequired
}

export default InterstitialUpdatePassword
